import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
} from 'react';
import { Button, Form } from '@bedrock/components';
import { isEqual } from 'lodash';
import { useControllableValue, useMemoizedFn, useDebounceEffect } from 'ahooks';
import classNames from 'classnames/bind';
import {
  PropertyRefDto,
  DemandCooperatePropertyValueDto,
  commonFilterOptions,
  DemandCooperatePropertiesVo,
  TemplateStandardItemDto,
} from '@mpc/common/src/services/api';
import { $sType } from '@mpc/common';
import styles from './index.less';
import FormItemDescriptions, {
  FormItemDescriptionsItemType,
} from '#components/DescriptionsFormItem';
import { getPropertyFieldConfig, getStandardFieldConfig } from './propertyFieldConfig';

const cx = classNames.bind(styles);

type FormValueItem = Record<string, any>;

type CooperationFormListProps = {
  schema?: DemandCooperatePropertiesVo;
  view?: boolean; // 是否是编辑类型
  value?: DemandCooperatePropertyValueDto[];
  onChange?: (value: DemandCooperatePropertyValueDto[]) => void;
  editType?: 'default' | 'returnToBusiness'; // 编辑类型
  structuredVersion?: number;
};

// formValue转换为合作内容值
const formValueToCooperationValue = (
    formValue: FormValueItem,
    schema: DemandCooperatePropertiesVo
) => {
  const standard_values =
      schema?.standards?.map?.((schemaItem) => {
        const value = formValue?.[schemaItem.code] ?? [];
        return {
          property_code: schemaItem.code,
          standard_schema: schemaItem,
          value: Array.isArray(value) ? value : [value],
          type: 1,
        } as DemandCooperatePropertyValueDto;
      }) || [];
  const property_values =
      schema?.properties?.map?.((schemaItem) => {
        const value = formValue?.[schemaItem.property_code] ?? [];
        return {
          property_code: schemaItem.property_code,
          property_schema: schemaItem,
          value: Array.isArray(value) ? value : [value],
          type: 0,
        } as DemandCooperatePropertyValueDto;
      }) || [];
  return standard_values.concat(property_values);
};

// 根据property_type修改数据模型，如果property_type为dropdown_list或者date_range，则value为数组，否则为单个值
const modifyDataModel = (properties: PropertyRefDto[], values: Record<string, any>) => {
  const newValues = {...values};
  const arrCodeMap = {}
  properties?.forEach?.((item) => {
    if (['dropdown_list', 'date_range'].includes(item.property_type)) {
      arrCodeMap[item.property_code] = item.property_code;
    }
  })
  Object.keys(newValues).forEach((key) => {
    if (!arrCodeMap[key]) {
      newValues[key] = newValues[key]?.[0] ?? newValues[key]
    }
  })
  return newValues;
}

// 合作内容值转换为formValue
const cooperationValueToFormValue = (
    cooperationValues: DemandCooperatePropertyValueDto[]
) => {
  const property_values =
      cooperationValues?.reduce((result, property) => {
        // 返回值默认是数组，如果长度为1，转换为单个值
        result[property.property_code] =
            property.value?.length > 1 ? property.value : property.value?.[0];
        return result || '';
      }, {}) || {};
  return property_values;
};

// CooperationValueItem的property_values对齐schema
export const alignPropertyValues = (
    property_values: DemandCooperatePropertyValueDto[],
    schema: DemandCooperatePropertiesVo
): DemandCooperatePropertyValueDto[] => {
  const standards =
      schema?.standards?.map((schemaItem) => {
        const standard =
            property_values.find((n) => n.property_code === schemaItem.code) || {};
        return {
          value: [],
          property_code: schemaItem.code,
          standard_schema: schemaItem,
          label: schemaItem.label,
          type: 1,
          ...standard,
        } as DemandCooperatePropertyValueDto;
      }) || [];
  const properties =
      schema?.properties?.map((schemaItem) => {
        const property =
            property_values.find(
                (n) => n.property_code === schemaItem.property_code
            ) || {};
        return {
          value: [],
          property_code: schemaItem.property_code,
          property_schema: schemaItem,
          type: 0,
          label: schemaItem.property_name,
          ...property,
        } as DemandCooperatePropertyValueDto;
      }) || [];
  return standards.concat(properties);
};

// 根据property_type生成FormItemDescriptionsItemType（字段配置元数据下沉至 propertyFieldConfig.ts，
// 折叠模式与表格模式共用同一份 property_type → 控件/校验映射，此处仅做 FormItemDescriptionsItemType 适配）
const propertyTypeToComponentConfig = (
    schemaItem: PropertyRefDto,
    formValues?: Record<string, any[]>,
    structuredVersion?: number
): FormItemDescriptionsItemType => {
  const { controlType, ...config } = getPropertyFieldConfig(schemaItem, formValues, structuredVersion);
  return {
    formItemKey: schemaItem.property_code,
    label: schemaItem.property_name,
    ...config,
  };
};

const standardTypeToComponentConfig = (
    schemaItem: TemplateStandardItemDto,
    schemaStandards?: TemplateStandardItemDto[],
    formValues?: Record<string, any>,
): FormItemDescriptionsItemType => {
  const { controlType, ...config } = getStandardFieldConfig(schemaItem, formValues);
  return {
    formItemKey: schemaItem?.code,
    required: schemaItem?.required,
    label: schemaItem?.label,
    ...config,
  };
};

const CooperationPropertyForm = forwardRef(function (
    props: CooperationFormListProps,
    ref
) {
  const [dataList, setDataList] = useControllableValue<
      DemandCooperatePropertyValueDto[]
  >(props, {
    defaultValue: [],
  });
  // 外部数据变化，更新本地数据
  const [localDataList, setLocalDataList] = useState([]);
  const {
    view = false,
    schema: parentSchema = null,
    editType = 'default',
    structuredVersion,
  } = props;

  const [form] = Form.useForm();

  useEffect(() => {
    if (isEqual(dataList, localDataList)) {
      return;
    } else {
      form.setFieldsValue(cooperationValueToFormValue(dataList));
    }
  }, [dataList, localDataList]);

  const [disabledFields, setDisabledFields] = useState<string[]>([]);
  // useEffect(() => {
  //   // 编辑类型为退回商务确认报价确认节点时，获取不可编辑字段
  //   if (editType === 'returnToBusiness') {
  //     commonFilterOptions({ keys: ['cooperate_account_edit_disable'] }).then(
  //       (res: any) => {
  //         const temp =
  //           res?.data?.[0]?.options?.map?.((item) => item.value) || [];
  //         setDisabledFields(temp);
  //       }
  //     );
  //   }
  // }, [editType]);

  const [schema, setSchema] = useState<DemandCooperatePropertiesVo | null>(
      null
  );

  const localSchema = useMemo(() => {
    if (!dataList?.length) {
      return {};
    }
    const property_values = dataList || [];
    const standards =
        property_values
            .map((n) => n.standard_schema)
            .filter((n) => !!n)
            ?.map?.((conf) => {
          if (disabledFields.includes(conf?.code)) {
            conf.view = true;
          }
          return conf;
        }) || [];
    const properties =
        property_values
            .map((n) => n.property_schema)
            .filter((n) => !!n)
            ?.map?.((conf) => {
          if (disabledFields.includes(conf?.property_code)) {
            conf.view = true;
          }
          return conf;
        }) || [];
    return { properties, standards } as DemandCooperatePropertiesVo;
  }, [dataList, disabledFields]);

  useEffect(() => {
    if (!view && parentSchema) {
      // 跟新schema
      setSchema(parentSchema);
    } else {
      setSchema(localSchema);
    }
  }, [parentSchema, view, localSchema]);

  const mergeSchema = useMemoizedFn(() => {
    let isPropertiesDifferent = false;
    let isStandardsDifferent = false;

    if (schema?.properties?.length && localSchema?.properties?.length) {
      const localSchemaKeys = localSchema?.properties
          ?.map((n) => n?.property_code)
          .sort();
      const schemaKeys = schema?.properties?.map((n) => n.property_code).sort();
      isPropertiesDifferent =
          localSchemaKeys.join(',') !== schemaKeys.join(',');
    }

    if (schema?.standards?.length && localSchema?.standards?.length) {
      const localSchemaKeys = localSchema?.standards
          ?.map((n) => n?.code)
          .sort();
      const schemaKeys = schema?.standards?.map((n) => n.code).sort();
      isStandardsDifferent = localSchemaKeys.join(',') !== schemaKeys.join(',');
    }

    // 如果schema不一样，对齐数据的schema
    if (isPropertiesDifferent || isStandardsDifferent) {
      setDataList(alignPropertyValues(dataList, schema));
    }
  });


  useEffect(() => {
    if(!dataList?.length && schema?.properties?.length) {
      onFormChange({},{})
    }
  }, [schema, dataList]);

  // 修复时序竞态：schema 准备好时重新将 dataList 回填到内部 form
  // 场景：EditModal 中 form.setFieldsValue(values) 和 setSchema(builtSchema) 不是同步到达的，
  // value 先到但此时内部 schema 为 null 导致控件未渲染，schema 后到时需重新回填
  useEffect(() => {
    if (dataList?.length && schema?.properties?.length) {
      form.setFieldsValue(cooperationValueToFormValue(dataList));
    }
  }, [schema]);

  // 数据和schema对齐,模型改变则清空，不再进行数据合并
  // useDebounceEffect(
  //     () => {
  //       mergeSchema();
  //     },
  //     [dataList, schema],
  //     {
  //       wait: 40,
  //     }
  // );

  const onFormChange = useMemoizedFn((changedValues, allValues) => {
    if (!schema) {
      return;
    }
    const newValues = formValueToCooperationValue(allValues, schema);
    const mergedValues = newValues.map((newVal) => {
      const original = dataList?.find(
        (p: DemandCooperatePropertyValueDto) => p.property_code === newVal.property_code
      );
      if (original?.disable_edit !== undefined) {
        return { ...newVal, disable_edit: original.disable_edit };
      }
      return newVal;
    });
    setDataList(mergedValues);
    setLocalDataList(mergedValues);
  });

  /**
   * 派生展示（级联 options / 显隐 / 校验规则）用的行内值 —— **必须从 dataList 派生，
   * 不能读 `form.getFieldsValue()`**（#46339）。
   *
   * 内部 form 是靠 effect（上方 :183 / :285）异步 `setFieldsValue` 填充的，而 `setFieldsValue`
   * 只通知订阅了具体字段的 Field，**不会让本组件重渲染**；上面那段 `setSchema(parentSchema)`
   * 又因引用不变被 React 跳过更新（bail out）。两者叠加 → 渲染体里裸调 `form.getFieldsValue()`
   * 会**停在「store 还是空」的那一次计算**：级联属性（standardCode≥2，如「合作项目」）算不出
   * options → 下拉「暂无选项」→ Select 兜底把原值当 label，显示成裸编码 `SPXP001-002`。
   *
   * 这里用的表达式与填 store 的完全相同（同一个 cooperationValueToFormValue + 同一份 dataList），
   * 取值与 form 内的值逐字段等价，差别只是不再滞后于数据到达 —— 与表格模式
   * （propertyColumns.tsx 的 formMap）同源同语义。
   */
  const formValues = useMemo(() => cooperationValueToFormValue(dataList), [dataList]);

  const standardItems =
      schema?.standards?.map((schemaItem) => {
        return {
          formItemKey: schemaItem.code,
          // view = true 时全部只读，view = false 时单字段只读控制
          view: view ? true : !!schemaItem.view,
          ...standardTypeToComponentConfig(schemaItem, schema?.standards, formValues),
        };
      }) || [];

  const propertyItems =
      schema?.properties?.map((schemaItem) => {
        const propertyValue = dataList?.find(
          (p: DemandCooperatePropertyValueDto) => p.property_code === schemaItem.property_code
        );
        return {
          formItemKey: schemaItem.property_code,
          // view = true 时全部只读，view = false 时单字段只读控制
          view: view ? true : (!!schemaItem.view || !!propertyValue?.disable_edit),
          ...propertyTypeToComponentConfig(schemaItem, formValues, structuredVersion),
        };
      }) || [];

  const items = [...standardItems, ...propertyItems];

  useImperativeHandle(ref, () => ({
    validateFields: () => {
      form.clearValidate?.();
      return form.validateFields().catch((error) => {
        return Promise.reject(undefined);
      });
    },
  }));

  return (
      <div>
        {/* <Button type="primary" onClick={() => {
        console.log(form);
        form.validateFields().then((res) => {
          console.log('success', res);
        }).catch((error) => {
          console.log('error', error);
        });
      }}>
        获取表单值
      </Button> */}
        <Form
            form={form}
            onValuesChange={onFormChange}
            initialValues={{}}
            style={{ width: '100%' }}
        >
          {!!items?.length && (
              <FormItemDescriptions
                  lightBorder
                  className={cx('collapse-content-table')}
                  column={2}
                  items={items}
              />
          )}
        </Form>
      </div>
  );
});

export default CooperationPropertyForm;
